import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useAtom } from 'jotai/react'

import { RPCEndpointKey, SUPPORT_RPC_ENDPOINT } from '@/constants/settings'
import { staticSchems } from '@/hooks/useSchems'
import { RpcEndpoint, solanaProvider } from '@/lib/solana/lib/providerFactory'
import { cn } from '@/lib/utils'
import { rpcSettingAtom } from '@/stores/settings'

import { Flex, Grid } from '../common/Box'
import { BeautyButton, Button } from '../common/Button'
import DropdownMenu from '../common/DropdownMenu'
import { Input } from '../common/Input'
import { Dividing } from '../common/Structure'
import { ErrorText } from '../common/Text'
import { Close, Setting } from '../svgr/common'
import { Label } from '../ui/label'
import { RadioGroup, RadioGroupItem } from '../ui/radio-group'

import PreferredExplorerSelect from './PreferredExplorerSelect'
import { SubTitle, Title } from './Title'

const GlobalSettings: React.FC<{
  className?: string
  wrapperClassName?: string
}> = ({ wrapperClassName, className }) => {
  const { t } = useTranslation()
  const [rpcSetting, setRpcSetting] = useAtom(rpcSettingAtom)
  const [invalidUrlState, setInvalidUrlState] = useState<boolean>(false)

  const [open, setOpen] = useState(false)
  const [formData, setFormData] = useState<{
    rpcKey?: string
    rpcUrl?: string
  }>({
    rpcKey: '',
    rpcUrl: ''
  })

  const customInputRef = useRef<HTMLInputElement>(null)

  const memoFormChanged = useMemo(() => {
    // const a = (rpcSetting || {}) as any
    // const b = formData as any
    // const flag = Object.keys(formData).some((key: string) => {
    //   return a[key] != b[key]
    // })
    // return flag
    return (
      rpcSetting?.rpcKey !== formData.rpcKey ||
      (formData.rpcKey === RPCEndpointKey.Custom &&
        rpcSetting?.rpcUrl !== formData.rpcUrl)
    )
  }, [rpcSetting, formData])

  const setRpcEndpoint = useCallback((value: string) => {
    const item = SUPPORT_RPC_ENDPOINT.find((item) => item.key === value)
    setFormData({
      rpcKey: item?.key,
      rpcUrl: item?.url
    })
  }, [])

  const onInputRpc = useCallback((ev: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => {
      return {
        ...prev,
        rpcUrl: ev.target.value
      }
    })
  }, [])

  const saveRpcEndpoint = useCallback(() => {
    const endpointItem = SUPPORT_RPC_ENDPOINT.find(
      (item) => item.key === formData.rpcKey
    )
    if (!endpointItem) {
      return
    }
    if (endpointItem?.key === RPCEndpointKey.Custom) {
      const val = (formData.rpcUrl || '').trim()
      let url
      try {
        url = staticSchems.urlSchema.parse(val)
      } catch {
        setInvalidUrlState(true)
        return
      }
      endpointItem.url = url
    }
    setRpcSetting({
      rpcKey: endpointItem.key,
      rpcUrl: endpointItem.url
    })
    solanaProvider.switchRpc(endpointItem as RpcEndpoint)
    toast.success(t('setting.message.rpcUpdated'))
    setOpen(false)
  }, [formData, setRpcSetting, t])

  useEffect(() => {
    if (open) {
      setRpcEndpoint(rpcSetting.rpcKey)
      setFormData({
        rpcKey: rpcSetting.rpcKey,
        rpcUrl: rpcSetting.rpcUrl
      })
    }
  }, [open])

  return (
    <DropdownMenu
      open={open}
      onOpenChange={setOpen}
      content={{
        className: 'xl:w-[448px] w-[286px] relative',
        onInteractOutside: (event) => event.preventDefault()
      }}
      trigger={{
        asChild: true,
        children: (
          <BeautyButton
            beauty={false}
            wrapperClassName={wrapperClassName}
            className={className}
          >
            <Setting
              className={cn(
                'transition-transform',
                open ? 'rotate-90' : 'rotate-0'
              )}
            />
          </BeautyButton>
        )
      }}
    >
      <button
        type="button"
        className="absolute right-2 top-2 flex size-8 items-center justify-center xl:right-4 xl:top-4"
        onClick={() => setOpen(false)}
      >
        <Close />
      </button>
      <Grid className="gap-4 xl:gap-6">
        <Title>{t('settings')}</Title>

        <Grid className="gap-4 xl:gap-4">
          <Flex className="items-center justify-between">
            <SubTitle>{t('settings.preferredExplorer')}</SubTitle>
            <PreferredExplorerSelect />
          </Flex>
          <Dividing />
          <Grid className="gap-4 xl:gap-4">
            <SubTitle>{t('settings.RPCEndpoint')}</SubTitle>
            <RadioGroup
              value={formData.rpcKey}
              onValueChange={(value) => setRpcEndpoint(value)}
              className="gap-4 xl:gap-4 xl:pl-3"
            >
              {SUPPORT_RPC_ENDPOINT.map(({ name, key }) => (
                <Flex key={key} className="items-center gap-2 xl:gap-6">
                  <RadioGroupItem
                    value={key}
                    id={key}
                    className="size-4 xl:size-6 [&_svg]:size-2 xl:[&_svg]:size-3"
                  />
                  <Label
                    htmlFor={key}
                    className="font-Orbitron text-xs xl:text-base"
                  >
                    {name}
                  </Label>
                </Flex>
              ))}
            </RadioGroup>
            <div>
              <Input
                ref={customInputRef}
                size="md"
                className="font-Orbitron"
                suffixNode={
                  <Button
                    border={false}
                    size="xs"
                    className="font-Orbitron -mr-2 text-xs"
                    disabled={!memoFormChanged}
                    onClick={saveRpcEndpoint}
                  >
                    {t('common.save')}
                  </Button>
                }
                placeholder={t('settings.customRPCURL')}
                value={formData.rpcUrl}
                readOnly={formData?.rpcKey !== RPCEndpointKey.Custom}
                onChange={onInputRpc}
                onFocus={() => setInvalidUrlState(false)}
              />
              {invalidUrlState && (
                <ErrorText>{t('setting.message.invalidURL')}</ErrorText>
              )}
            </div>
          </Grid>
        </Grid>
      </Grid>
    </DropdownMenu>
  )
}

export default GlobalSettings
